import { StaticCanvas } from 'fabric';
import { preloadFonts } from '../../text/utils/preload-fonts';
import { ResolutionConfig } from '../../shared/types/binding-types';
import { CanvasJSON } from '../../canvas/types';
import { prepareCanvasJson } from '../../canvas/utils/prepare-canvas-json';
import { applyCanvasFrameClip } from '../../viewport/utils/canvas-frame-clip';

/**
 * Encodes the current Fabric canvas as a PNG and returns its binary contents.
 * Fabric exposes the PNG as a data URL, so `fetch` converts that base64
 * representation into a Blob suitable for downloads and uploads.
 */
export async function canvasToPngBlob(canvas: StaticCanvas): Promise<Blob> {
  // Encode the rendered pixels as a base64 PNG data URL.
  const dataUrl = canvas.toDataURL({ format: 'png', multiplier: 1 });
  const res = await fetch(dataUrl);

  // Represent the PNG as a binary file in memory.
  return res.blob();
}

export async function renderResolutionsToBlobs(
  resolutions: ResolutionConfig[],
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>,
  onProgress?: (current: number, total: number) => void
): Promise<Array<{ resolution: ResolutionConfig; blob: Blob }>> {
  // This Fabric canvas lives in memory and is not mounted in the editor UI.
  // Despite the variable name, it is not the browser's native OffscreenCanvas.
  // Reusing it keeps exports isolated from the interactive editor canvas.
  const offscreen = new StaticCanvas();
  try {
    const results: Array<{ resolution: ResolutionConfig; blob: Blob }> = [];

    for (let i = 0; i < resolutions.length; i++) {
      const resolution = resolutions[i];

      const json = await prepareCanvasJson(await resolveResolutionJson(resolution));
      await preloadFonts(json);
      await offscreen.loadFromJSON(json);
      offscreen.setDimensions({ width: resolution.canvasWidth, height: resolution.canvasHeight });

      // Prevent objects outside the frame from appearing in the exported image.
      applyCanvasFrameClip(offscreen, resolution.canvasWidth, resolution.canvasHeight);
      offscreen.requestRenderAll();

      const blob = await canvasToPngBlob(offscreen);
      results.push({ resolution, blob });
      onProgress?.(i + 1, resolutions.length);
    }

    return results;
  } finally {
    offscreen.dispose();
  }
}
